import React, { useState, useEffect, useCallback, useContext } from "react";
import { Link } from "react-router-dom";
import { URL, getToken } from "../../utils";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import Select from "react-select";
import CountryField from "../../components/CountryField";
import { useCountries } from "../../hooks/useCountries";
import { UserContext } from "../../contexts/UserContext";

export default function ProspectAddForm() {
  const [formData, setFormData] = useState({
    nom: "",
    poste: "",
    sexe: "",
    secteur_activite: "Company",
    telephone: "",
    adresse: "", // Ajout du champ adresse (non obligatoire)
    qualifie: "Non",
    is_entite: "Non",
    rendez_vous: "Non",
    date_rendez_vous: "",
    commercial: 0,
    magasin: null,
    date_creation: new Date().toISOString(),
    pays: "",
    commentaire: "",
  });

  const [commercialOptions, setCommercialOptions] = useState([
    {
      value: null,
      label: "",
    },
  ]);
  const [magasinOptions, setMagasinOptions] = useState([]);
  const [isLoadingCommercials, setIsLoadingCommercials] = useState(false);
  const [isLoadingMagasins, setIsLoadingMagasins] = useState(false);
  const navigate = useNavigate();
  const token = getToken();
  const [validationErrors, setValidationErrors] = useState({});
  const { countries } = useCountries();
  const { user } = useContext(UserContext);

  const fetchCommercials = useCallback(async () => {
    setIsLoadingCommercials(true);
    try {
      const response = await axios.get(
        `${URL}Magasins/commercial/liste-commerciaux`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const options = response.data.map((commercial) => ({
        value: commercial.id,
        label: `${commercial.nom} ${commercial.prenom}`,
      }));
      setCommercialOptions(options);
    } catch (error) {
      toast.error("Erreur lors du chargement des commerciaux");
    } finally {
      setIsLoadingCommercials(false);
    }
  }, [token]);

  const fetchMagasins = useCallback(async () => {
    setIsLoadingMagasins(true);
    try {
      const response = await axios.get(`${URL}Magasins/magasins-list`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const options = response.data.map((magasin) => ({
        value: magasin.id,
        label: `${magasin.nom} - ${magasin.quartier}`,
      }));
      setMagasinOptions(options);
    } catch (error) {
      toast.error("Erreur lors du chargement des magasins");
    } finally {
      setIsLoadingMagasins(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCommercials();
    if (!user.is_caissier) {
      fetchMagasins();
    }
  }, [fetchCommercials, fetchMagasins, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCommercialChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      commercial: selectedOption.value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.commercial) {
      toast.error("Veuillez sélectionner un commercial");
      return;
    }

    const payload = {
      ...formData,
      date_rendez_vous:
        formData.rendez_vous === "Oui"
          ? (formData.date_rendez_vous ?? null)
          : null,
    };

    axios
      .post(`${URL}Prospects/prospect-create`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          toast.success("Prospect ajouté avec succès !");
          navigate("/prospects");
        }
      })
      .catch((error) => {
        if (error.status === 400) {
          toast.error("Erreur de validation des données");
          setValidationErrors(error.response.data);
        } else {
          toast.error(error.response?.data?.error || "Erreur lors de l'ajout");
        }
      });
  };

  // Style personnalisé pour react-select
  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "42px",
      borderRadius: "0.5rem",
      borderColor: "#d1d5db",
      "&:hover": {
        borderColor: "#d1d5db",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "#b45309" : "white",
      color: state.isSelected ? "white" : "#1f2937",
      "&:hover": {
        backgroundColor: "#fcd34d",
        color: "#1f2937",
      },
    }),
  };

  return (
    <div className="p-4 w-[min(600px,90%)] mx-auto mt-4 bg-white shadow-md rounded-md">
      <ToastContainer />
      <div className="flex gap-4 p-4 pb-0">
        <div className="p-3 bg-amber-200 rounded-full text-amber-600 grid justify-center items-center">
          <svg
            className="w-8 h-8"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M16 12h4m-2 2v-4M4 18v-1a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3v1a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1Zm8-10a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
            />
          </svg>
        </div>
        <div className="">
          <h1 className="text-lg font-semibold">Ajouter un prospect</h1>
          <p className="text-sm text-gray-500">
            Ajoutez un nouveau prospect à la liste
          </p>
        </div>
      </div>

      {/* Bloc récapitulatif des erreurs en haut du formulaire */}
      <ErrorSummary errors={validationErrors} />

      <form className="p-4" onSubmit={handleSubmit}>
        {/* Nom complet */}
        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-900">
            Nom complet
          </label>
          <input
            type="text"
            name="nom"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            value={formData.nom}
            onChange={handleChange}
            required
          />
        </div>

        {/* Champs pour date de creation du prospect */}
        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-900">
            Date de création
          </label>
          <input
            type="date"
            name="date_creation"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            value={formData.date_creation}
            onChange={handleChange}
          />
        </div>

        {/* Téléphone */}
        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-900">
            Téléphone
          </label>
          <input
            type="tel"
            name="telephone"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            value={formData.telephone}
            onChange={handleChange}
            required
          />
        </div>

        <div className="mb-4">
          <CountryField
            label="Pays"
            value={formData.pays}
            onChange={(selectedOption) =>
              setFormData((prev) => ({
                ...prev,
                pays: selectedOption ? selectedOption.value : "",
              }))
            }
            countries={countries}
            required
          />
        </div>

        {/* Adresse */}
        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-900">
            Adresse
          </label>
          <input
            type="text"
            name="adresse"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            value={formData.adresse || ""}
            onChange={handleChange}
            placeholder="Adresse (optionnelle)"
          />
        </div>

        {/* Commercial */}
        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-900">
            Commercial
          </label>
          <Select
            options={commercialOptions}
            isLoading={isLoadingCommercials}
            onChange={handleCommercialChange}
            placeholder="Rechercher un commercial..."
            noOptionsMessage={() => "Aucun résultat"}
            styles={customStyles}
            isSearchable
          />
        </div>

        {/* Magasin - seulement si l'utilisateur n'est pas caissier */}
        {!user.is_caissier && (
          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium text-gray-900">
              Magasin{" "}
              <span className="text-red-500 text-xl font-bold pl-1">*</span>
            </label>
            <Select
              options={magasinOptions}
              isLoading={isLoadingMagasins}
              onChange={(selectedOption) =>
                setFormData((prev) => ({
                  ...prev,
                  magasin: selectedOption ? selectedOption.value : null,
                }))
              }
              placeholder="Sélectionner un magasin..."
              noOptionsMessage={() => "Aucun résultat"}
              isClearable
              styles={customStyles}
            />
            {validationErrors.magasin && (
              <p className="text-red-500 text-sm">{validationErrors.magasin}</p>
            )}
          </div>
        )}

        {/* Qualifié */}
        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-900">
            Qualifié
          </label>
          <select
            name="qualifie"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            value={formData.qualifie}
            onChange={handleChange}
          >
            <option value="Oui">Oui</option>
            <option value="Non">Non</option>
          </select>
        </div>

        {/* Entité */}
        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-900">
            Est une entité
          </label>
          <select
            name="is_entite"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            value={formData.is_entite}
            onChange={handleChange}
          >
            <option value="Oui">Oui</option>
            <option value="Non">Non</option>
          </select>
        </div>

        {formData.is_entite === "Oui" ? (
          <>
            {/* Secteur d'activité */}
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-900">
                Secteur d'activité
              </label>
              <select
                name="secteur_activite"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
                value={formData.secteur_activite}
                onChange={handleChange}
              >
                <option value="shop">Boutique</option>
                <option value="bar">Bar</option>
                <option value="restaurant">Restaurant</option>
                <option value="hotel">Hôtel</option>
                <option value="Company">Entreprise</option>
                <option value="night_etablishement">
                  Établissement de nuit
                </option>
              </select>
            </div>
          </>
        ) : (
          <>
            {/* Poste */}
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-900">
                Poste
              </label>
              <input
                type="text"
                name="poste"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
                value={formData.poste}
                onChange={handleChange}
              />
            </div>

            {/* Sexe */}
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-900">
                Sexe
              </label>
              <select
                name="sexe"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
                value={formData.sexe}
                onChange={handleChange}
              >
                <option value="">Sélectionner</option>
                <option value="M">Masculin</option>
                <option value="F">Féminin</option>
              </select>
            </div>
          </>
        )}

        {/* Rendez-vous */}
        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-900">
            Rendez-vous
          </label>
          <select
            name="rendez_vous"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            value={formData.rendez_vous}
            onChange={handleChange}
          >
            <option value="Oui">Oui</option>
            <option value="Non">Non</option>
          </select>
        </div>

        {/* Date de rendez-vous (conditionnel) */}
        {formData.rendez_vous === "Oui" && (
          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium text-gray-900">
              Date de rendez-vous
            </label>
            <input
              type="datetime-local"
              name="date_rendez_vous"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
              value={formData.date_rendez_vous}
              onChange={handleChange}
              required={formData.rendez_vous === "Oui"}
            />
          </div>
        )}

        {/* Commentaire */}

        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-900">
            Commentaire
          </label>
          <textarea
            name="commentaire"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            value={formData.commentaire}
            onChange={handleChange}
          />
        </div>

        <div className="grid grid-cols-2 items-center gap-4 mt-6">
          <Link
            to="/prospects"
            className="hover:text-white border border-gray-800 bg-gray-200 hover:bg-gray-900 focus:outline-none focus:ring-4 focus:ring-gray-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
          >
            Annuler
          </Link>
          <button
            type="submit"
            className="text-white bg-amber-700 hover:bg-amber-800 focus:ring-4 focus:outline-none focus:ring-amber-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center"
          >
            Ajouter
          </button>
        </div>
      </form>
    </div>
  );
}

// Composant ErrorSummary.jsx
function ErrorSummary({ errors }) {
  // Convertir l'objet d'erreurs en tableau
  const errorMessages = Object.values(errors).flat();

  if (errorMessages.length === 0) return null;

  return (
    <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
      <div className="flex items-start">
        <svg
          className="w-5 h-5 text-red-500 mt-0.5 mr-2 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <div>
          <h3 className="text-red-800 font-medium mb-2">
            {errorMessages.length > 1
              ? `${errorMessages.length} erreurs doivent être corrigées`
              : "1 erreur doit être corrigée"}
          </h3>
          <ul className="list-disc list-inside space-y-1 text-red-700 text-sm">
            {errorMessages.map((message, index) => (
              <li key={index}>{message}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
